/**
 * Maps pose metrics → coaching cues grounded in the knowledge artifact.
 * Pure functions — no DOM.
 */

import knowledge from "../knowledge/coaching-knowledge.json";
import type { CoachingCue, CoachingResult, PoseMetrics } from "./types";

export type Knowledge = typeof knowledge;

export function getKnowledge(): Knowledge {
  return knowledge;
}

interface CueDef {
  id: string;
  text: string;
  metric: string;
  when: string;
  priority: number;
  themes: string[];
  relatedMistakeIds: string[];
}

interface Threshold {
  good?: number;
  warn?: number;
  bad?: number;
  goodAbs?: number;
  warnAbs?: number;
  badAbs?: number;
}

function metricValue(metrics: PoseMetrics, key: string): number | undefined {
  const v = (metrics as unknown as Record<string, unknown>)[key];
  return typeof v === "number" ? v : undefined;
}

/**
 * Decide whether a cue's `when` condition fires for the current metric value.
 */
export function cueConditionMet(
  when: string,
  value: number,
  threshold: Threshold | undefined,
): boolean {
  const t = threshold ?? {};
  switch (when) {
    case "high": {
      const cut = t.warn ?? t.bad ?? 0.5;
      return value >= cut;
    }
    case "low": {
      const cut = t.warn ?? t.good ?? 0.5;
      return value <= cut;
    }
    case "high_positive": {
      const cut = t.warnAbs ?? t.badAbs ?? 0.06;
      return value >= cut;
    }
    case "high_negative": {
      const cut = -(t.warnAbs ?? t.badAbs ?? 0.06);
      return value <= cut;
    }
    default:
      return false;
  }
}

/**
 * Select coaching cues from knowledge data for the given metrics.
 * Returns cues sorted by priority (highest first), capped.
 */
export function selectCues(
  metrics: PoseMetrics,
  options: { maxCues?: number; knowledge?: Knowledge } = {},
): CoachingCue[] {
  const kb = options.knowledge ?? knowledge;
  const maxCues = options.maxCues ?? 3;
  const thresholds = kb.metricThresholds as Record<string, Threshold>;
  const cueDefs = kb.cues as CueDef[];

  const fired: CoachingCue[] = [];

  for (const def of cueDefs) {
    const value = metricValue(metrics, def.metric);
    if (value === undefined) continue;
    const th = thresholds[def.metric];
    if (!cueConditionMet(def.when, value, th)) continue;

    // Special case: good_stack only when inverted and quality high
    if (def.id === "cue_good_stack" && metrics.inverted < 0.45) continue;

    fired.push({
      id: def.id,
      text: def.text,
      priority: def.priority,
      metric: def.metric,
      themes: def.themes,
      relatedMistakeIds: def.relatedMistakeIds,
    });
  }

  fired.sort((a, b) => b.priority - a.priority);

  // Prefer one high-priority correction over the "good" cue when both fire.
  const corrections = fired.filter((c) => c.id !== "cue_good_stack");
  if (corrections.length > 0) {
    return corrections.slice(0, maxCues);
  }
  return fired.slice(0, maxCues);
}

/**
 * Full coaching step: metrics in → grounded cues out.
 */
export function coachFromMetrics(
  metrics: PoseMetrics,
  options: { maxCues?: number; knowledge?: Knowledge } = {},
): CoachingResult {
  const cues = selectCues(metrics, options);
  return {
    cues,
    metrics,
    primaryCue: cues[0] ?? null,
  };
}

/** Lookup cue text by id from knowledge (for tests / UI). */
export function cueTextById(id: string, kb: Knowledge = knowledge): string | undefined {
  const found = (kb.cues as CueDef[]).find((c) => c.id === id);
  return found?.text;
}

/** All cue ids present in knowledge. */
export function allCueIds(kb: Knowledge = knowledge): string[] {
  return (kb.cues as CueDef[]).map((c) => c.id);
}
